import {
  Box,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { CardTransactionStatus } from "Infrastructure/Api/Api";
import { CardStatusFilter } from "Components/CardTransactions/CardTransactionFilters/CardStatusFilter";
import type { CardTransactionFilters } from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import { DateRangeFilter } from "Components/Shared/Filters/DateRangeFilter";
import { EmployeeFilter } from "Components/Shared/Filters/EmployeeFilter";
import { FilterActions } from "Components/Shared/Filters/FilterActions";
import type { FilterOption } from "Components/Shared/Filters/FilterOption";
import { PayPointFilter } from "Components/Shared/Filters/PayPointFilter";
import { RefundOrCancellationFilter } from "Components/Shared/Filters/RefundOrCancellationFilter";
import { TimePeriodFilter } from "Components/Shared/Filters/TimePeriodFilter";
import type { TimePeriod } from "Types/TimePeriod";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Shared.Filters;

type Props = {
  isOpen: boolean;
  localFilters: CardTransactionFilters;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
  onTimePeriodChange: (value: TimePeriod | undefined) => void;
  onPayPointChange: (option?: FilterOption) => void;
  onEmployeeChange: (option?: FilterOption) => void;
  onStatusChange: (value?: CardTransactionStatus) => void;
  onRefundOrCancellationChange: (value: boolean) => void;
  onDateFromChange: (value?: Date) => void;
  onDateToChange: (value?: Date) => void;
};

export const CardTransactionFiltersDrawer = ({
  isOpen,
  localFilters: {
    timePeriod,
    payPoint,
    dateFrom,
    dateTo,
    employee,
    status,
    isRefundOrCancellation,
  },
  onClose,
  onApply,
  onReset,
  onTimePeriodChange,
  onPayPointChange,
  onEmployeeChange,
  onStatusChange,
  onRefundOrCancellationChange,
  onDateFromChange,
  onDateToChange,
}: Props) => {
  const { t } = useResource();

  // A custom period without both dates would silently apply as "no period filter at all" (the derived
  // dateFrom/dateTo end up undefined) while the chip still claims a bounded range — block Apply until the
  // range is complete.
  const isApplyDisabled = timePeriod === "custom" && (!dateFrom || !dateTo);

  return (
    <Drawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "80vh",
          },
        },
      }}
    >
      <Stack p={3} pb={4} gap={3} width="100%" maxWidth={600} mx="auto">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {t(RESOURCES.Title)}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Grid container spacing={3}>
          <TimePeriodFilter value={timePeriod} onChange={onTimePeriodChange} />
          <PayPointFilter value={payPoint} onChange={onPayPointChange} />
          {timePeriod === "custom" && (
            <DateRangeFilter
              dateTo={dateTo}
              dateFrom={dateFrom}
              onDateFromChange={onDateFromChange}
              onDateToChange={onDateToChange}
            />
          )}
          <EmployeeFilter value={employee} onChange={onEmployeeChange} />
          <CardStatusFilter value={status} onChange={onStatusChange} />
          <RefundOrCancellationFilter
            value={isRefundOrCancellation ?? false}
            onChange={onRefundOrCancellationChange}
          />
        </Grid>
        <Box mt={3}>
          <FilterActions
            onApply={onApply}
            onReset={onReset}
            isApplyDisabled={isApplyDisabled}
          />
        </Box>
      </Stack>
    </Drawer>
  );
};
